import { Box, Stack } from '@mui/material';
import {
    ConfirmationDialog,
    PoolButton,
    PoolFormattedMessage,
    PoolModal,
    PoolSelect,
    SelectOption,
} from '@synergy/components';
import { Employee } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { ReactNode, useEffect, useState } from 'react';
import { StyledMobileStepper } from '@synergy/components-next';

enum StepType {
    MainContact,
    BonusContact,
    ImportContact,
}

interface Step {
    type: StepType;
    content: ReactNode;
}

export interface RemoveEmployeeModalProps {
    isVisible: boolean;
    employees: Employee[];
    employee: Employee | null;
    onConfirm: (
        reassignMainContactId?: number,
        reassignBonusContactId?: number,
        reassignImportContactId?: number
    ) => void;
    onCancel: () => void;
}

export const RemoveEmployeeModal = (props: RemoveEmployeeModalProps) => {
    const { isVisible, employees, employee, onConfirm, onCancel } = props;
    const [selectedBonusEmployeeId, setSelectedBonusEmployeeId] = useState<number>();
    const [selectedMainEmployeeId, setSelectedMainEmployeeId] = useState<number>();
    const [selectedImportEmployeeId, setSelectedImportEmployeeId] = useState<number>();
    const [steps, setSteps] = useState<Step[]>([]);
    const [activeStep, setActiveStep] = useState(0);

    const options = props.employees
        .filter(employee => employee.id !== props.employee?.id && !employee.isLocked)
        .map<SelectOption>(employee => ({
            value: employee.id,
            displayValue: employee.fullName,
        }));

    useEffect(() => {
        if (!isVisible || !employee || !employees.length || employees.length === 1) return;

        const newSteps = [];
        if (employee.isMainContact) {
            newSteps.push({
                type: StepType.MainContact,
                content: (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <PoolFormattedMessage
                                id="app.contactsAndOrganizations.detail.removePersonMessageMainContact"
                                values={{ name: employee.fullName }}
                            />
                        </Box>
                        <PoolSelect
                            options={options}
                            fullWidth={true}
                            value={selectedMainEmployeeId ?? ''}
                            onValueChange={setSelectedMainEmployeeId}
                            label={<FormattedMessage id="app.contactsAndOrganizations.detail.selectMainContact" />}
                            disallowDefaultValue={true}
                        />
                    </>
                ),
            });
        }
        if (employee.hasBonusConditions) {
            newSteps.push({
                type: StepType.BonusContact,
                content: (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <PoolFormattedMessage
                                id="app.contactsAndOrganizations.detail.removePersonMessageBonus"
                                values={{ name: employee.fullName }}
                            />
                        </Box>
                        <PoolSelect
                            options={options}
                            fullWidth={true}
                            value={selectedBonusEmployeeId ?? ''}
                            onValueChange={setSelectedBonusEmployeeId}
                            label={<FormattedMessage id="app.contactsAndOrganizations.detail.selectBonusContact" />}
                            disallowDefaultValue={true}
                        />
                    </>
                ),
            });
        }

        if (employee.isImportContact) {
            newSteps.push({
                type: StepType.ImportContact,
                content: (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <PoolFormattedMessage
                                id="app.contactsAndOrganizations.detail.removePersonMessageImport"
                                values={{ name: employee.fullName }}
                            />
                        </Box>
                        <PoolSelect
                            options={options}
                            fullWidth={true}
                            value={selectedImportEmployeeId ?? ''}
                            onValueChange={setSelectedImportEmployeeId}
                            label={<FormattedMessage id="app.contactsAndOrganizations.detail.selectImportContact" />}
                            disallowDefaultValue={true}
                        />
                    </>
                ),
            });
        }
        setSteps(newSteps);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employee, options, selectedMainEmployeeId, selectedBonusEmployeeId, selectedImportEmployeeId]);

    const disabledStep =
        (steps[activeStep]?.type === StepType.BonusContact && !selectedBonusEmployeeId) ||
        (steps[activeStep]?.type === StepType.ImportContact && !selectedImportEmployeeId) ||
        (steps[activeStep]?.type === StepType.MainContact && !selectedMainEmployeeId);

    if (steps.length === 0) {
        return (
            <ConfirmationDialog
                title={<FormattedMessage id="app.contactsAndOrganizations.detail.removePerson" />}
                action={<FormattedMessage id="app.common.delete" />}
                isVisible={isVisible}
                onCancel={onCancel}
                onConfirm={() => onConfirm()}
            >
                <PoolFormattedMessage
                    id="app.contactsAndOrganizations.detail.removePersonMessage"
                    values={{ name: employee?.fullName }}
                />
            </ConfirmationDialog>
        );
    }

    if (steps.length === 1) {
        return (
            <PoolModal
                title={<FormattedMessage id="app.contactsAndOrganizations.detail.removePerson" />}
                isVisible={isVisible}
                handleClose={onCancel}
                actionChildren={[
                    <span key="1" />,
                    <PoolButton
                        key="2"
                        color="secondary"
                        styleType="firstLevel"
                        disabled={disabledStep}
                        onClick={() =>
                            onConfirm(selectedMainEmployeeId, selectedBonusEmployeeId, selectedImportEmployeeId)
                        }
                    >
                        <FormattedMessage id="app.common.delete" />
                    </PoolButton>,
                ]}
            >
                <Stack sx={{ textAlign: 'center' }}>{steps[activeStep]?.content}</Stack>
            </PoolModal>
        );
    }

    return (
        <PoolModal
            title={<FormattedMessage id="app.contactsAndOrganizations.detail.removePerson" />}
            isVisible={isVisible}
            handleClose={onCancel}
            actionChildren={
                <StyledMobileStepper
                    activeStep={activeStep}
                    steps={steps.length}
                    variant="dots"
                    backButton={
                        <PoolButton
                            variant="text"
                            color="primary"
                            styleType="firstLevel"
                            disabled={activeStep === 0}
                            onClick={() => setActiveStep(prevActiveStep => prevActiveStep - 1)}
                        >
                            <FormattedMessage id="app.common.back" />
                        </PoolButton>
                    }
                    nextButton={
                        activeStep === steps.length - 1 ? (
                            <PoolButton
                                color="secondary"
                                styleType="firstLevel"
                                disabled={disabledStep}
                                onClick={() =>
                                    onConfirm(selectedMainEmployeeId, selectedBonusEmployeeId, selectedImportEmployeeId)
                                }
                            >
                                <FormattedMessage id="app.common.delete" />
                            </PoolButton>
                        ) : (
                            <PoolButton
                                color="primary"
                                styleType="firstLevel"
                                disabled={disabledStep}
                                onClick={() => setActiveStep(prevActiveStep => prevActiveStep + 1)}
                            >
                                <FormattedMessage id="app.common.next" />
                            </PoolButton>
                        )
                    }
                />
            }
        >
            <Stack sx={{ textAlign: 'center' }}>{steps[activeStep]?.content}</Stack>
        </PoolModal>
    );
};
